import { Koi } from '@ksyos/koi';
import type { Field, Section } from '@common/formEngine/formEngine.types';
import type { IPatientContactDetails } from '@common/interfaces/patientContactDetails';
import type { PatientAlternativeAddressType } from '@common/validation/customControls/patientContactDetailsSchema';
import { Gender } from '@common/enums';
import { OpnameDuur } from '@common/enums/opnameDuur';
import { typeOnderzoek } from '@common/enums/typeOnderzoek';
import { YesNoNoDeviations } from '@common/enums/yesNoDeviations';
import { dependsOn } from '@common/formEngine/dependsOn';
import { initialAddress } from '@common/validation/address';
import {
    patienAlternativeAddressDraftSchema,
    patientAlternativeAddressFinalSchema,
    patientContactDetailsDraftSchema,
    patientContactDetailsFinalSchema,
    sendToAlternativeAddressSchema,
} from '@common/validation/customControls/patientContactDetailsSchema';
import type {
    HolterExaminationRequestAdvice,
    HolterExaminationRequestBase,
    HolterReferralData,
    HolterRiskFactorsData,
} from './utils/utils';
import { getGeneralPractitionerSchema } from '../../generalPractitioner';
import { type HolterExaminationRequest, professionalSummarySectionFields } from './hollterRequestsSchemas';
import {
    FamilyRiskFactorOption,
    familyRiskFactorOptions,
    FemaleRiskFactorOption,
    femaleRiskFactorOptions,
    GeneralRiskFactorOption,
    generalRiskFactorOptions,
    yesNoNoDeviationsOptions,
} from './utils/utils';

export const referralSectionBaseFields: Array<Field<HolterReferralData, undefined, HolterExaminationRequest>> = [
    {
        draftSchema: getGeneralPractitionerSchema(true),
        finalSchema: Koi.any(),
        include: dependsOn(
            'patientData.isPatientGeneralPractitionerLoggedIn',
            ({ patientData }) => !patientData.isPatientGeneralPractitionerLoggedIn
        ),
        initialValue: {
            organizationId: null,
            practitionerId: null,
        },
        isRequired: true,
        key: 'generalPractitioner',
        label: 'Huisarts van de patiënt',
        type: 'custom',
    },
];

export const holteReferralSection: Section<HolterReferralData, undefined, HolterExaminationRequest> = {
    fields: referralSectionBaseFields,
};

export const holterSetupSection: Section<HolterExaminationRequestBase, undefined, HolterExaminationRequest> = {
    fields: [
        {
            draftSchema: Koi.koi().enum(Gender).allow(null),
            finalSchema: Koi.when('patientData.gender', {
                is: Koi.valid(Gender.male, Gender.female),
                otherwise: Koi.koi().enum(Gender),
                then: Koi.valid(null),
            }),
            include: dependsOn(
                'patientData.gender',
                ({ patientData }) => patientData.gender !== Gender.male && patientData.gender != Gender.female
            ),
            initialValue: null,
            isRequired: true,
            key: 'patient_geslacht',
            label: 'Geslacht',
            type: 'custom',
        },
        {
            isRequired: true,
            key: 'type_onderzoek',
            label: 'Type onderzoek',
            layout: 'row',
            tooltipText:
                'Een holteronderzoek registreert de hartactiviteit continu voor 1 tot 3 dagen om ritmestoornissen te detecteren. Een eventonderzoek registreert continu voor 7 dagen en wordt ingezet als de klachten sporadisch voorkomen.',
            type: 'radioGroup',
            valueOptions: typeOnderzoek,
        },
        {
            draftSchema: Koi.koi().enum(OpnameDuur).allow(null),
            finalSchema: Koi.koi().enum(OpnameDuur),
            initialValue: null,
            key: 'opnameduur',
            label: 'Opnameduur',
            type: 'custom',
        },
    ],
    title: 'Onderzoek',
};

export type HolterExaminationRequestContentKeys = 'adviesAlert';

export const adviceSection: Section<
    HolterExaminationRequestAdvice,
    HolterExaminationRequestContentKeys,
    HolterExaminationRequest
> = {
    fields: [
        {
            isRequired: true,
            key: 'meedenkadvies_cardioloog',
            label: 'Meedenkadvies van de cardioloog bij afwijkende beoordeling',
            type: 'boolean',
        },

        {
            include: dependsOn('meedenkadvies_cardioloog'),
            key: 'adviesAlert',
            type: 'content',
        },
        {
            include: dependsOn('meedenkadvies_cardioloog'),
            isRequired: true,
            key: 'vraag_aan_specialist',
            label: 'Vraag aan de cardioloog',
            type: 'textArea',
        },
    ],
    title: 'Adviesvraag',
    tooltipContent:
        'Als je ‘ja’ kiest, starten we automatisch een consult zodra de cardioloog een afwijking vaststelt. Je ontvangt dan aanvullend advies bij de diagnose.',
};

export const holterRiskFactorsSection: Section<
    HolterRiskFactorsData & HolterExaminationRequestAdvice,
    undefined,
    HolterExaminationRequest
> = {
    fields: [
        {
            checkboxEnumType: GeneralRiskFactorOption,
            include: dependsOn('meedenkadvies_cardioloog'),
            isRequired: false,
            key: 'general_riskfactors',
            label: 'Algemene risicofactoren',
            type: 'nestedCheckboxGroup',
            valueOptions: generalRiskFactorOptions,
        },
        {
            checkboxEnumType: FemaleRiskFactorOption,
            include: dependsOn(
                ['patientData.gender', 'meedenkadvies_cardioloog'],
                (data) => data.patientData.gender !== Gender.male && data.meedenkadvies_cardioloog === true
            ),
            initialValue: {
                hypertension_before_menopause: false,
                hypertension_during_pregnancy: false,
                menopause: false,
                miscarriage: false,
            },
            isRequired: false,
            key: 'female_riskfactors',
            label: 'Vrouwelijke risicofactoren',
            type: 'checkboxGroup',
            valueOptions: femaleRiskFactorOptions,
        },
        {
            checkboxEnumType: FamilyRiskFactorOption,
            include: dependsOn('meedenkadvies_cardioloog'),
            isRequired: false,
            key: 'familial_riskfactors',
            label: 'Familiaire risicofactoren',
            type: 'checkboxGroup',
            valueOptions: familyRiskFactorOptions,
        },
        {
            include: dependsOn('meedenkadvies_cardioloog'),
            key: 'riskfactors_remark',
            label: 'Toelichting risicofactoren',
            type: 'textArea',
        },
    ],
    title: 'Risicofactoren',
};

export const professionalSummarySection: Section<
    HolterExaminationRequestBase & HolterExaminationRequestAdvice,
    undefined,
    HolterExaminationRequest
> = {
    fields: professionalSummarySectionFields,
    include: dependsOn('meedenkadvies_cardioloog'),
    title: 'Professionele samenvatting',
};

export const diagnosisSection: Section<HolterExaminationRequestAdvice> = {
    fields: [
        {
            include: dependsOn('meedenkadvies_cardioloog'),
            isRequired: true,
            key: 'hasDiagnosis',
            label: 'Diagnose bekend',
            layout: 'row',
            type: 'radioGroup',
            valueOptions: yesNoNoDeviationsOptions,
        },

        {
            include: dependsOn('hasDiagnosis', ({ hasDiagnosis }) => hasDiagnosis === YesNoNoDeviations.yes),
            isRequired: true,
            key: 'diagnosis_icd10',
            label: 'Werkdiagnose',
            type: 'icd10',
        },
    ],
    title: 'Diagnose',
};

export const patientAddressSection: Section<
    PatientAlternativeAddressType & { patient: IPatientContactDetails },
    undefined,
    HolterExaminationRequest
> = {
    fields: [
        {
            draftSchema: patientContactDetailsDraftSchema,
            finalSchema: patientContactDetailsFinalSchema,
            initialValue: { ...initialAddress, emailAddress: null, homePhoneNumber: null, mobileNumber: null },
            isRequired: true,
            key: 'patient',
            label: 'Contactgegevens patiënt',
            type: 'custom',
        },
        {
            draftSchema: sendToAlternativeAddressSchema,
            finalSchema: sendToAlternativeAddressSchema,
            initialValue: null,
            key: 'sendToAlternativeAddress',
            label: 'Verstuur recorder naar ander adres',
            type: 'custom',
        },
        {
            draftSchema: patienAlternativeAddressDraftSchema,
            finalSchema: patientAlternativeAddressFinalSchema,
            initialValue: { ...initialAddress },
            key: 'alternativeAddress',
            label: 'Alternatief adres',
            type: 'custom',
        },
    ],
};
